import {
  Node as ReactFlowNode,
  NodePositionChange,
  useStoreApi,
} from '@xyflow/react';
import { useCallback, useMemo } from 'react';

import {
  AlignKind,
  computeSectionAwareAlignmentChanges,
  computeSectionAwareDistributionChanges,
  DistributeAxis,
  NodePositionUpdate,
  SectionedAlignableNode,
} from '../utils/alignment';
import { getNodeMeasuredWidthHeight, isTileNode } from '../utils/nodes';
import { isTextNoteNode } from '../utils/textNoteUtils';

interface UseAlignNodesOptions<T extends ReactFlowNode> {
  /**
   * Which node types may participate in align/distribute (applied on top of
   * the `selected` filter). Defaults to tile and text-note nodes, which covers
   * the entity diagram and entity map; the family tree passes its own
   * predicate for person/marriage-group nodes. Pass a stable (module-level)
   * function so the memoized selection doesn't churn.
   */
  isEligible?: (node: T) => boolean;
}

function isTileOrTextNoteNode(node: ReactFlowNode): boolean {
  return isTileNode(node) || isTextNoteNode(node);
}

/**
 * Sectioned diagrams encode a node's vertical band as its id prefix
 * (`afterDeath:id`, matching gridLayout). Text notes also use a `:` in their
 * ids (`text-note:<uuid>`) but are free-floating annotations, not band
 * members, so they align globally. Ids without a `:` (entity map, family
 * tree) have no section.
 */
function getNodeSection(node: ReactFlowNode): string | undefined {
  if (isTextNoteNode(node)) return undefined;
  const delimiterIndex = node.id.indexOf(':');
  if (delimiterIndex === -1) return undefined;
  return node.id.slice(0, delimiterIndex);
}

/**
 * Shared hook for bulk align/distribute of selected nodes, used by the
 * diagrams outside the estate waterfall. Y-axis alignment and distribution
 * are section-aware (see `computeSectionAwareAlignmentChanges`), so sectioned
 * layouts like the single-entity diagram keep nodes within their death bands
 * while flat layouts degenerate to plain global alignment. Emits position
 * changes via `triggerNodeChanges` so they flow through each diagram's
 * `onNodesChange` → reducer path — mirroring the `useBulkTileSizeChange`
 * pattern in this folder.
 *
 * The estate waterfall has its own version at
 * `src/modules/estateWaterfall/hooks/useAlignNodes.ts` that dispatches
 * ALIGN_NODES/DISTRIBUTE_NODES through its reducer for undo/redo support.
 */
export function useAlignNodes<T extends ReactFlowNode>(
  nodes: T[],
  options?: UseAlignNodesOptions<T>
) {
  const storeApi = useStoreApi();
  const isEligible = options?.isEligible ?? isTileOrTextNoteNode;

  const eligibleNodes = useMemo(
    () => nodes.filter((node) => !!node.selected && isEligible(node)),
    [nodes, isEligible]
  );

  const sectionCount = useMemo(
    () => new Set(eligibleNodes.map(getNodeSection)).size,
    [eligibleNodes]
  );

  const canAlign = eligibleNodes.length >= 2;
  // Distribution is limited to a single section (or an entirely sectionless
  // selection) — equalizing gaps across different vertical bands shuffles
  // nodes with no visual meaning.
  const canDistribute = eligibleNodes.length >= 3 && sectionCount === 1;

  const applyPositionUpdates = useCallback(
    (updates: NodePositionUpdate[]) => {
      if (updates.length === 0) return;

      // dragging:false keeps helper-line snapping inert; applyNodeChanges
      // preserves the `selected` flags so the toolbar stays up.
      const changes: NodePositionChange[] = updates.map(({ id, position }) => ({
        id,
        type: 'position',
        position,
        dragging: false,
      }));
      storeApi.getState().triggerNodeChanges(changes);
    },
    [storeApi]
  );

  const toAlignable = useCallback(
    (): SectionedAlignableNode[] =>
      eligibleNodes.map((node) => ({
        id: node.id,
        position: node.position,
        section: getNodeSection(node),
        ...getNodeMeasuredWidthHeight(node),
      })),
    [eligibleNodes]
  );

  const alignNodes = useCallback(
    (kind: AlignKind) => {
      applyPositionUpdates(
        computeSectionAwareAlignmentChanges(toAlignable(), kind)
      );
    },
    [applyPositionUpdates, toAlignable]
  );

  const distributeNodes = useCallback(
    (axis: DistributeAxis) => {
      applyPositionUpdates(
        computeSectionAwareDistributionChanges(toAlignable(), axis)
      );
    },
    [applyPositionUpdates, toAlignable]
  );

  return {
    eligibleCount: eligibleNodes.length,
    canAlign,
    canDistribute,
    alignNodes,
    distributeNodes,
  };
}
